import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { renderEnergyInfrastructureLayer } from "../lib/energyInfrastructureLayer";
import { addGrayscaleBasemapControl } from "../lib/mapBasemap";
import "leaflet/dist/leaflet.css";
import { LAYER_GROUPS, buildCountryTileUrls } from "../lib/geozoneLayers";
import {
  vectorFeaturesBounds,
  renderEd269Layer,
  filterEd269Features,
  filterExpiredEd269Features,
  loadGeozoneShowAll,
  saveGeozoneShowAll,
} from "../lib/ed269Web";
import { ed269IsTemporary } from "../../../shared/ed269Restriction";
import { DEFAULT_REACHABLE_ALTITUDE_M } from "../../../shared/geozoneAltitudeFilter";
import { ed269SourceLabelFor } from "../lib/ed269SourceLabel";
import { fetchElevation } from "@client/lib/elevation";
import { fetchNotams, geocodeSearch, reverseGeocode, type GeocodeResult } from "../lib/external";
import {
  GEOZONE_REGULATORY_INFO,
  getContactsForLayerId,
  type GeozoneRegulatoryInfo,
  type GeozoneContact,
} from "@client/lib/geozoneRegulatoryInfo";
import { describeHeightSteps, DFS_CTR_MAX_HEIGHT_AGL_M, DFS_ZONEN_HEIGHT_STEPS, getCtrBandRings, getCtrLegendEntries, getCtrProvisionalNote, isDfsAipGeometryComplete, isDfsZonenEffective } from "@client/lib/ctrAltitude";
import { CTR_AIRPORTS, type CtrAvInfo } from "@client/lib/ctrData";
import { getVectorMinZoom, COUNTRY_WMS_MAX_NATIVE_ZOOM } from "@client/lib/geozoneZoom";
import { infoFormatForCountry, renderFeatureInfo, type Country } from "../lib/geozoneFeatureInfo";
import { useShowDataSources } from "../lib/dataSource";
import { useGermanEnergyInfrastructure } from "../lib/useGermanEnergyInfrastructure";
import type { GermanEnergyInfrastructureItem } from "@shared/germanEnergyInfrastructure";
import { getEnergyInfrastructureCountry } from "../../../shared/germanEnergyInfrastructure";
import { detectCountryFromCoordinates } from "../../../shared/countryDetection";
import { entitlementHasCountry, useSubscription } from "../lib/subscription";
import {
  GEOZONE_CATEGORIES,
  COMBINED_COUNTRY_TOGGLES,
  filterActiveWmsLayers,
  defaultGeozoneSelection,
  mergeSelectionWithDefaults,
  migrateSelection,
  type OldLayerRef,
} from "../../../shared/geozoneCategories";

// Ordnet eine Web-Layer-ID (geozoneLayers.ts) dem passenden Schluessel in
// GEOZONE_REGULATORY_INFO zu (= dieselbe geteilte Datenbasis wie Mobile).
// LU hat keine kuratierte Info und ist daher bewusst nicht enthalten.
const REG_INFO_KEY_BY_LAYER: Record<string, string> = {
  de_kontrollzonen: "kontrollzonen",
  de_edr: "flugbeschraenkungsgebiete",
  de_temp: "temporaere_betriebseinschraenkungen",
  de_flughaefen: "flughaefen",
  de_flugplaetze: "flugplaetze",
  de_naturschutz: "naturschutzgebiete",
  de_nationalparks: "nationalparks",
  de_ffh: "ffh-gebiete",
  de_militaer: "militaerische_anlagen",
  de_strom: "stromleitungen",
  de_kraftwerke: "kraftwerke",
  de_wind: "windkraftanlagen",
  de_autobahnen: "bundesautobahnen",
  de_bahn: "bahnanlagen",
  de_modellflug: "modellflugplaetze",
  de_wohn: "wohngrundstuecke",
  ch_drohnen: "ch_drohnen",
  ch_uas: "ch_aktivitaetszonen",
  ch_ruhe: "ch_ruhezonen",
  ch_wohn: "ch_wohngebiete",
  nl_no_fly: "nl_no_fly_zones",
  fr_restrictions: "fr_restrictions",
};

const EXTERNAL_MAPS = [
  { id: "dipul", label: "DFS Dipul (DE)", url: "https://maptool-dipul.dfs.de/" },
  { id: "geoportail-lu", label: "Geoportail (LU)", url: "https://drones.geoportail.lu/" },
  { id: "swissdrones", label: "BAZL Karte (CH)", url: "https://map.geo.admin.ch/?topic=aviation&layers=ch.bazl.einschraenkungen-drohnen" },
] as const;
type TabId = "interactive" | (typeof EXTERNAL_MAPS)[number]["id"];

// Fix default marker icons (Leaflet expects asset URLs from a CDN by default)
const ICON_URL = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png";
const ICON_RETINA = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png";
const SHADOW_URL = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png";
const DefaultIcon = L.icon({
  iconRetinaUrl: ICON_RETINA,
  iconUrl: ICON_URL,
  shadowUrl: SHADOW_URL,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function infrastructurePopup(
  item: GermanEnergyInfrastructureItem,
  officialSource: string,
  countryIso: string,
): HTMLElement {
  const root = document.createElement("div");
  const title = document.createElement("strong");
  title.textContent = item.name;
  root.appendChild(title);

  const comparison =
    item.officialMatch === "matched"
      ? `Abgleich: Treffer in der Nähe in ${officialSource}`
      : item.officialMatch === "not_found"
        ? `Abgleich: nicht in ${officialSource} bestätigt`
        : `Abgleich: mit ${officialSource} nicht sicher möglich`;
  const details = document.createElement("p");
  details.textContent = `${item.source} · ${comparison} · ${Math.round(item.distanceMeters)} m vom Kartenzentrum`;
  root.appendChild(details);

  if (countryIso === "DE") {
    const legalNote = document.createElement("p");
    legalNote.textContent =
      "§ 21h Abs. 3 Nr. 3 LuftVO: In der Nähe zentraler Energieanlagen gelten besondere Voraussetzungen. Die Kartendistanz ist nur ein Hinweis; maßgeblich ist die Anlagenbegrenzung.";
    root.appendChild(legalNote);
  }
  return root;
}

function infrastructureMatchColor(source: string): string {
  return source === "MaStR" ? "#7652A8" : "#2878B5";
}

const DEFAULT_CENTER: [number, number] = [50.5, 9.0];
const DEFAULT_ZOOM = 6;
const INFRASTRUCTURE_MIN_ZOOM = 10;
const STORAGE_KEY = "copter.map.layers.v2";
// Alter pro-Layer-Speicher (vor der Kategorie-Umstellung). Wird einmalig auf den
// neuen kategoriebasierten Speicher migriert (OR-Faltung).
const LEGACY_STORAGE_KEY = "copter.map.layers.v1";

// Geozonen-Overlays werden ab dieser Zoomstufe geladen. Seit die Zonen als
// EINE serverseitig gecachte Kombi-Kachel pro Land geladen werden (nicht mehr
// ~23 separate WMS-Layer), ist Heraus-/Hineinzoomen unkritisch. Daher = der
// Karten-minZoom (5), damit die Zonen über den gesamten Zoombereich sichtbar
// bleiben — der WMS rendert auch auf Übersichtsebenen (empirisch geprüft).
const WMS_MIN_ZOOM = 5;
const WMS_MAX_ZOOM = 18;

// Höchste Zoomstufe, die der jeweilige Anbieter tatsächlich rendert. Darüber
// skaliert Leaflet die letzte echte Kachel hoch, statt leere nachzuladen.
// FR (DGAC TRANSPORTS.DRONES.RESTRICTIONS) rendert zuverlässig bis Zoom 14;
// ab Zoom 15 kommen leere Kacheln (geprüft: z14=Inhalt, z15=leer). Wert kommt
// aus dem GETEILTEN, React-Native-freien Modul (= dasselbe wie native
// wmsMaxNativeZoom in client/lib/geozones.ts), damit beide nicht driften.
const COUNTRY_MAX_NATIVE_ZOOM: Record<string, number> = COUNTRY_WMS_MAX_NATIVE_ZOOM;

// Vektor-Geozonen (ED-269: AT/BE/DK) werden — wie auf der nativen Radarkarte —
// PRO LAND ab unterschiedlichen Zoomstufen gezeichnet: dichte Länder (DK) erst
// ab dem Default (Timeout-Schutz der bbox-Abfrage), kleine Länder (BE, ~288
// Zonen) bereits bei landesweiter Sicht. Die Schwelle kommt aus der GETEILTEN,
// React-Native-freien Funktion getVectorMinZoom, damit Web und Native nicht
// auseinanderdriften.

// Begrenzt das Pan-Gebiet grob auf D-A-CH + Benelux, damit beim Wegscrollen
// nicht endlos Tiles nachgeladen werden.
const MAP_BOUNDS: L.LatLngBoundsExpression = [
  [45.5, 4.0],
  [55.5, 17.5],
];

// ED-269-Vektor-Geozonen-Helfer (vectorFeaturesBounds/renderEd269Layer) + die
// Default-AN-Layer leben jetzt in ../lib/ed269Web (geteilt mit der Planungs-
// Karte), damit beide Oberflächen nicht auseinanderdriften.

// Beschreibt die früher pro-Layer gespeicherten Web-Layer (id = alter Speicher-
// Schlüssel) für die einmalige Migration auf Kategorien.
function legacyOldLayerRefs(): OldLayerRef[] {
  return LAYER_GROUPS.flatMap((g) =>
    g.layers.map((l) => ({ id: l.id, iso: g.country, wmsLayer: l.layer })),
  );
}

function loadEnabled(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      // Bestehende Kategorie-Auswahl: nur fehlende Schlüssel mit Defaults füllen.
      return mergeSelectionWithDefaults(JSON.parse(raw) as Record<string, boolean>);
    }
    // Einmalige Migration der alten pro-Layer-Auswahl (v1) auf Kategorien.
    const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw) as Record<string, boolean>;
      const migrated = migrateSelection(legacy, legacyOldLayerRefs());
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      } catch {}
      return migrated;
    }
  } catch {}
  return defaultGeozoneSelection();
}

export default function MapPage() {
  const isAdmin = useShowDataSources();
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Eine kombinierte, serverseitig gecachte Kachel-Ebene pro Land.
  const countryTileLayersRef = useRef<Record<string, { layer: L.TileLayer; key: string }>>({});
  const markerRef = useRef<L.Marker | null>(null);
  const notamLayerRef = useRef<L.LayerGroup | null>(null);
  const lfaLayerRef = useRef<L.LayerGroup | null>(null);
  const atLayerRef = useRef<L.LayerGroup | null>(null);
  const beLayerRef = useRef<L.LayerGroup | null>(null);
  // BE: serverseitig rasterisierte PNG-Kachel-Ebene für DAUERHAFTE Zonen (wie
  // WMS). beLayerRef bleibt für temporäre skeyes-Zonen (orange Vektor-Polygone).
  const beTileLayerRef = useRef<L.TileLayer | null>(null);
  const dkLayerRef = useRef<L.LayerGroup | null>(null);
  const ctrBandLayerRef = useRef<L.LayerGroup | null>(null);
  const infrastructureLayerRef = useRef<L.LayerGroup | null>(null);
  const [mapVersion, setMapVersion] = useState(0);

  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => loadEnabled());
  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [address, setAddress] = useState<string>("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("interactive");
  const [showInfrastructure, setShowInfrastructure] = useState(false);
  const [infrastructureCenter, setInfrastructureCenter] = useState<{
    lat: number;
    lon: number;
    zoom: number;
  } | null>(null);
  const [ctrLegendOpen, setCtrLegendOpen] = useState(true);
  // Aktueller Karten-Zoom; Vektor-Geozonen werden pro Land ab getVectorMinZoom(iso)
  // gezeichnet, darunter ggf. "Hineinzoomen"-Hinweis.
  const [vectorZoom, setVectorZoom] = useState<number>(DEFAULT_ZOOM);
  const [vectorHint, setVectorHint] = useState(false);
  // Höhen-Filter für Vektor-Geozonen (AT/BE/DK): Zonen, deren Untergrenze über
  // der erreichbaren Höhe (Standard 120 m AGL) liegt, ausblenden. centerElevationM
  // dient der MSL->AGL-Umrechnung am Kartenzentrum.
  const [showAllGeozones, setShowAllGeozones] = useState<boolean>(() => loadGeozoneShowAll());
  const [centerElevationM, setCenterElevationM] = useState<number | null>(null);
  const { data: subscription } = useSubscription();
  const infrastructureIso = infrastructureCenter
    ? detectCountryFromCoordinates(infrastructureCenter.lat, infrastructureCenter.lon)
    : null;
  const infrastructureCountry = getEnergyInfrastructureCountry(infrastructureIso ?? "");
  const infrastructureCountryLocked =
    !!infrastructureIso &&
    !entitlementHasCountry(subscription?.entitlement, infrastructureIso);
  const energyInfrastructureQuery = useGermanEnergyInfrastructure(
    infrastructureCenter?.lat ?? 0,
    infrastructureCenter?.lon ?? 0,
    5,
    infrastructureIso ?? "",
    showInfrastructure &&
      activeTab === "interactive" &&
      !!infrastructureCenter &&
      infrastructureCenter.zoom >= INFRASTRUCTURE_MIN_ZOOM &&
      !!infrastructureCountry &&
      !infrastructureCountryLocked,
  );
  const ctrLegendEntries = useMemo(() => getCtrLegendEntries(), []);
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Initialize Leaflet map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      minZoom: 5,
      maxZoom: 18,
      maxBounds: MAP_BOUNDS,
      maxBoundsViscosity: 1.0,
      worldCopyJump: false,
    });
    const baseLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap-Mitwirkende",
      maxZoom: 19,
    }).addTo(map);
    addGrayscaleBasemapControl(map, baseLayer);
    notamLayerRef.current = L.layerGroup().addTo(map);
    lfaLayerRef.current = L.layerGroup().addTo(map);
    atLayerRef.current = L.layerGroup().addTo(map);
    beLayerRef.current = L.layerGroup().addTo(map);
    dkLayerRef.current = L.layerGroup().addTo(map);
    ctrBandLayerRef.current = L.layerGroup().addTo(map);
    infrastructureLayerRef.current = L.layerGroup().addTo(map);
    map.on("moveend", () => setMapVersion((v) => v + 1));
    map.on("click", async (e) => {
      const { lat, lng } = e.latlng;
      setPoint({ lat, lon: lng });
      await showFeatureInfoPopup(map, e.latlng, e.containerPoint, enabledRef.current);
    });
    mapRef.current = map;
    setMapVersion((version) => version + 1);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Fetch infrastructure only after the user opts in and the map stops moving.
  // The fixed 5 km radius and zoom gate keep country-wide queries off the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!showInfrastructure || activeTab !== "interactive" || !map) {
      setInfrastructureCenter(null);
      return;
    }
    setInfrastructureCenter(null);
    const center = map.getCenter();
    const zoom = map.getZoom();
    const timer = window.setTimeout(() => {
      setInfrastructureCenter({ lat: center.lat, lon: center.lng, zoom });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [showInfrastructure, activeTab, mapVersion]);

  // Draw the country-aware supplemental records on the interactive map. Popups
  // state the source and the official-source comparison without implying that
  // supplemental assets are themselves official geozones.
  useEffect(() => {
    const layer = infrastructureLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (
      !showInfrastructure ||
      activeTab !== "interactive" ||
      !infrastructureCenter ||
      infrastructureCenter.zoom < INFRASTRUCTURE_MIN_ZOOM ||
      infrastructureCountryLocked ||
      !energyInfrastructureQuery.data ||
      !infrastructureIso
    ) {
      return;
    }
    renderEnergyInfrastructureLayer(layer, energyInfrastructureQuery.data);
  }, [
    showInfrastructure,
    activeTab,
    infrastructureCenter?.zoom,
    infrastructureCountryLocked,
    infrastructureIso,
    energyInfrastructureQuery.data,
  ]);

  // Sync enabled geozone overlays: EINE serverseitig gecachte XYZ-Kachel-
  // Ebene pro Land (statt ~23 einzelner WMS-Ebenen, die den Browser beim
  // "alle Layer an" zum Absturz brachten). Der Server kombiniert/komponiert
  // die ausgewählten Layer (inkl. NL-Natura-2000) und legt sie dauerhaft ab.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // EINE Kachel-Ebene pro Land (alle aktiven Layer kombiniert) — die Bündelung
    // steckt in buildCountryTileUrls und ist separat getestet.
    const active = buildCountryTileUrls(enabled);
    const activeByCountry = new Map(active.map((a) => [a.country, a]));
    // Länder ohne aktive Layer (oder komplett abgewählt) wieder entfernen.
    for (const country of Object.keys(countryTileLayersRef.current)) {
      if (!activeByCountry.has(country as (typeof active)[number]["country"])) {
        map.removeLayer(countryTileLayersRef.current[country].layer);
        delete countryTileLayersRef.current[country];
      }
    }
    for (const entry of active) {
      const existing = countryTileLayersRef.current[entry.country];
      if (existing && existing.key === entry.layersParam) continue;
      if (existing) {
        map.removeLayer(existing.layer);
        delete countryTileLayersRef.current[entry.country];
      }
      const label = LAYER_GROUPS.find((g) => g.country === entry.country)?.label ?? entry.country;
      const tile = L.tileLayer(entry.url, {
        opacity: 0.7,
        minZoom: WMS_MIN_ZOOM,
        maxZoom: WMS_MAX_ZOOM,
        maxNativeZoom: COUNTRY_MAX_NATIVE_ZOOM[entry.country] ?? WMS_MAX_ZOOM,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 1,
        attribution: label,
      });
      tile.on("tileerror", () => {
        // Einzelne Tile-Fehler stillschweigend ignorieren statt die ganze
        // Karte abstürzen zu lassen.
      });
      tile.addTo(map);
      countryTileLayersRef.current[entry.country] = { layer: tile, key: entry.layersParam };
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
    } catch {}
  }, [enabled]);

  // Marker for current point
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (point) {
      markerRef.current = L.marker([point.lat, point.lon]).addTo(map);
    }
  }, [point]);

  // Reverse geocode + NOTAMs when point changes
  useEffect(() => {
    if (!point) return;
    let cancelled = false;
    setAddress("Lade Adresse …");
    reverseGeocode(point.lat, point.lon)
      .then((r) => {
        if (!cancelled) setAddress(r.display_name || `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`);
      })
      .catch(() => !cancelled && setAddress(`${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`));
    return () => {
      cancelled = true;
    };
  }, [point]);

  const notamsQuery = useQuery({
    queryKey: ["notams", point?.lat, point?.lon],
    queryFn: () => fetchNotams(point!.lat, point!.lon),
    enabled: !!point,
    staleTime: 5 * 60_000,
  });

  // LFA (Tieffluggebiete 250 ft) — wird nur geladen wenn der Layer aktiv ist
  const lfaQuery = useQuery<{ type: string; features: GeoJSON.Feature[] }>({
    queryKey: ["/api/lfa"],
    enabled: !!enabled["de_lfa"],
    staleTime: 60 * 60_000,
  });

  // Render LFA-Polygone als GeoJSON-Layer
  useEffect(() => {
    const layer = lfaLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!enabled["de_lfa"]) return;
    const fc = lfaQuery.data;
    if (!fc?.features?.length) return;
    const style = { color: "#7c3aed", weight: 1.5, fillColor: "#7c3aed", fillOpacity: 0.15, smoothFactor: 0 };
    L.geoJSON(fc as GeoJSON.FeatureCollection, {
      style: () => style,
      onEachFeature: (feat, lyr) => {
        const p = (feat.properties || {}) as { id?: string; name?: string; altitudeFt?: number };
        const html = `<div style="max-width:240px"><b>${escapeHtml(p.id || "LFA")}</b><br/><span style="font-size:12px;color:#475569">${escapeHtml(p.name || "")}</span><br/><span style="font-size:12px">Bundeswehr-Tiefflug GND — ${p.altitudeFt ?? 250} ft AGL (≈ ${Math.round((p.altitudeFt ?? 250) * 0.3048)} m)</span></div>`;
        lyr.bindPopup(html);
      },
    }).addTo(layer);
  }, [enabled, lfaQuery.data]);

  // Österreich (Austro Control / Dronespace, ED-269) — kein WMS, sondern
  // serverseitig gecachte Vektor-Geozonen. Eigener queryFn, weil der
  // Default-Fetcher den Query-String verwirft und der Endpoint
  // {countries:[{features:[]}]} statt einer FeatureCollection liefert.
  const atQuery = useQuery<GeoJSON.Feature[]>({
    queryKey: ["at-geozones"],
    enabled: !!enabled["at_geozones"],
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const r = await fetch("/api/cached/geozones?country=at", { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      const country = Array.isArray(data?.countries)
        ? data.countries.find((c: any) => (c?.country || "").toLowerCase() === "at") ?? data.countries[0]
        : null;
      const features = country?.features;
      return Array.isArray(features) ? (features as GeoJSON.Feature[]) : [];
    },
  });

  // Geländehöhe am Kartenzentrum für die MSL->AGL-Umrechnung des Höhen-Filters.
  // Nur laden, wenn gefiltert wird (showAll aus). Bei Kartenbewegung neu holen.
  useEffect(() => {
    if (showAllGeozones) return;
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    let cancelled = false;
    fetchElevation(c.lat, c.lng)
      .then((t) => { if (!cancelled) setCenterElevationM(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showAllGeozones, mapVersion]);

  // Abgelaufene Zonen (Gültigkeitsende in der Vergangenheit) IMMER ausblenden –
  // auch bei "Alle Geozonen anzeigen". Danach der optionale Höhen-Filter.
  const applyAltitudeFilter = (features: GeoJSON.Feature[]): GeoJSON.Feature[] => {
    const current = filterExpiredEd269Features(features);
    if (showAllGeozones) return current;
    return filterEd269Features(current, DEFAULT_REACHABLE_ALTITUDE_M, centerElevationM).visible;
  };

  // Render AT-Geozonen als GeoJSON-Layer mit restriktionsabhängiger Farbe
  // und Popup (Name, Beschränkung, Höhe). `hidden`-Zonen werden gefiltert.
  useEffect(() => {
    const layer = atLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!enabled["at_geozones"]) return;
    if (vectorZoom < getVectorMinZoom("AT")) return;
    const features = applyAltitudeFilter((atQuery.data || []).filter(
      (f) => f && f.geometry && (f.properties as any)?.hidden !== true,
    ));
    if (features.length === 0) return;
    renderEd269Layer(layer, features, ed269SourceLabelFor("at", isAdmin));
  }, [enabled, atQuery.data, vectorZoom, showAllGeozones, centerElevationM, isAdmin]);

  // Belgien (skeyes / Droneguide): serverseitig gecachte Vektor-Geozonen,
  // gleiche ED-269-Struktur wie AT. Quelle antwortet aktuell ggf. mit 401 –
  // dann liefert der Endpoint einfach keine Features (kein Fehler, keine Zonen).
  const beQuery = useQuery<GeoJSON.Feature[]>({
    queryKey: ["be-geozones"],
    enabled: !!enabled["be_geozones"],
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const r = await fetch("/api/cached/geozones?country=be", { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      const country = Array.isArray(data?.countries)
        ? data.countries.find((c: any) => (c?.country || "").toLowerCase() === "be") ?? data.countries[0]
        : null;
      const features = country?.features;
      return Array.isArray(features) ? (features as GeoJSON.Feature[]) : [];
    },
  });

  // BE-Geozonen werden zweigeteilt gezeichnet (Parität zur nativen Radar-Karte):
  // DAUERHAFTE Zonen kommen als serverseitig rasterisierte PNG-Kachel-Ebene (wie
  // WMS, robust statt fragiler Vektor-Polygone); nur TEMPORÄRE skeyes-Zonen
  // bleiben Leaflet-Vektor (orange), da sie nicht in den 24 h gecachten Kacheln
  // stecken. Aufteilung über die kanonische Regel (shared ed269IsTemporary).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (enabled["be_geozones"]) {
      if (!beTileLayerRef.current) {
        const tile = L.tileLayer("/api/proxy/geozone-vector-tiles/be/{z}/{x}/{y}.png", {
          opacity: 0.7,
          minZoom: WMS_MIN_ZOOM,
          maxZoom: WMS_MAX_ZOOM,
          updateWhenIdle: true,
          updateWhenZooming: false,
          keepBuffer: 1,
          attribution: ed269SourceLabelFor("be", isAdmin),
        });
        tile.on("tileerror", () => {});
        tile.addTo(map);
        beTileLayerRef.current = tile;
      }
    } else if (beTileLayerRef.current) {
      map.removeLayer(beTileLayerRef.current);
      beTileLayerRef.current = null;
    }
  }, [enabled, isAdmin]);

  useEffect(() => {
    const layer = beLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!enabled["be_geozones"]) return;
    if (vectorZoom < getVectorMinZoom("BE")) return;
    const features = applyAltitudeFilter((beQuery.data || []).filter(
      (f) =>
        f &&
        f.geometry &&
        (f.properties as any)?.hidden !== true &&
        // Nur temporäre Zonen als Vektor; dauerhafte zeichnet die Kachel-Ebene.
        // Kanonische Regel (shared ed269IsTemporary): Start ODER Ende gesetzt.
        ed269IsTemporary((f.properties || {}) as Record<string, any>),
    ));
    if (features.length === 0) return;
    renderEd269Layer(layer, features, ed269SourceLabelFor("be", isAdmin));
  }, [enabled, beQuery.data, vectorZoom, showAllGeozones, centerElevationM, isAdmin]);

  // Dänemark (Trafikstyrelsen): serverseitig gecachte Vektor-Geozonen. Die
  // Punkt+Bufferzone-Features sind bereits serverseitig zu Kreis-Polygonen
  // umgerechnet und deutsch normalisiert (Farve -> Beschränkung, Typ-Übersetzung
  // in `message`), sodass das Rendern identisch zu AT/BE läuft.
  const dkQuery = useQuery<GeoJSON.Feature[]>({
    queryKey: ["dk-geozones"],
    enabled: !!enabled["dk_geozones"],
    staleTime: 60 * 60_000,
    queryFn: async () => {
      const r = await fetch("/api/cached/geozones?country=dk", { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      const country = Array.isArray(data?.countries)
        ? data.countries.find((c: any) => (c?.country || "").toLowerCase() === "dk") ?? data.countries[0]
        : null;
      const features = country?.features;
      return Array.isArray(features) ? (features as GeoJSON.Feature[]) : [];
    },
  });

  useEffect(() => {
    const layer = dkLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!enabled["dk_geozones"]) return;
    if (vectorZoom < getVectorMinZoom("DK")) return;
    const features = applyAltitudeFilter((dkQuery.data || []).filter(
      (f) => f && f.geometry && (f.properties as any)?.hidden !== true,
    ));
    if (features.length === 0) return;
    renderEd269Layer(layer, features, ed269SourceLabelFor("dk", isAdmin));
  }, [enabled, dkQuery.data, vectorZoom, showAllGeozones, centerElevationM, isAdmin]);

  // Sichtbarkeit des "Hineinzoomen"-Hinweises + Zoom-Gate für Vektor-Geozonen.
  // Läuft bei jeder Karten-Bewegung (mapVersion) und Layer-/Datenänderung.
  const atBounds = useMemo(() => vectorFeaturesBounds(atQuery.data), [atQuery.data]);
  const beBounds = useMemo(() => vectorFeaturesBounds(beQuery.data), [beQuery.data]);
  const dkBounds = useMemo(() => vectorFeaturesBounds(dkQuery.data), [dkQuery.data]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const zoom = map.getZoom();
    setVectorZoom(zoom);
    // Hinweis nur für Länder, deren Zonen bei diesem Zoom NOCH NICHT gezeichnet
    // werden (zoom < pro-Land-Schwelle) und deren Bestand den Ausschnitt berührt.
    // Bereits sichtbare Länder (z. B. BE landesweit) lösen ihn nicht aus.
    const view = map.getBounds();
    const candidates = [
      enabled["at_geozones"] && zoom < getVectorMinZoom("AT") ? atBounds : null,
      enabled["be_geozones"] && zoom < getVectorMinZoom("BE") ? beBounds : null,
      enabled["dk_geozones"] && zoom < getVectorMinZoom("DK") ? dkBounds : null,
    ];
    setVectorHint(candidates.some((b) => b != null && view.intersects(b)));
  }, [mapVersion, enabled, atBounds, beBounds, dkBounds]);

  // Render NOTAM circles on map
  useEffect(() => {
    const layer = notamLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const rows = notamsQuery.data?.rows || [];
    for (const n of rows) {
      const popup = `<div style="max-width:260px"><b>${n.series || ""}${n.number || ""}/${n.year || ""}</b><br/><span style="font-size:12px;color:#475569">${(n.ItemQ || "").slice(0, 80)}</span><br/><span style="font-size:12px">${(n.ItemE || "").slice(0, 280)}</span></div>`;
      const style = { color: "#dc2626", weight: 2, fillColor: "#dc2626", fillOpacity: 0.28, smoothFactor: 0 };
      if (Array.isArray(n.points) && n.points.length >= 3) {
        L.polygon(n.points, style).bindPopup(popup).addTo(layer);
      } else if (n.lat != null && n.lon != null) {
        const radiusMeters = Math.max(((n.radius || 5) * 1852), 500);
        L.circle([n.lat, n.lon], { ...style, radius: radiusMeters }).bindPopup(popup).addTo(layer);
      }
    }
  }, [notamsQuery.data]);

  // Render der vorläufigen CTR-Innen-/Außenbereich-Höhenbänder als konzentrische
  // Kreise um die Flughafen-Referenzen (geteilte Logik getCtrBandRings). Wird
  // gemeinsam mit dem Kontrollzonen-WMS-Layer (de_kontrollzonen) ein-/ausgeblendet
  // und nur für Flughäfen im sichtbaren Ausschnitt gezeichnet. Größter Ring zuerst,
  // damit kleinere Kreise die größeren überdecken (sichtbare Bänder).
  useEffect(() => {
    const map = mapRef.current;
    const layer = ctrBandLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!enabled["de_kontrollzonen"]) return;
    if (map.getZoom() < 8) return;
    const bounds = map.getBounds().pad(0.25);
    for (const a of CTR_AIRPORTS) {
      if (!a.avInfo) continue;
      if (!bounds.contains([a.lat, a.lon])) continue;
      const rings = getCtrBandRings(a.avInfo.heightSteps);
      for (const ring of rings) {
        L.circle([a.lat, a.lon], {
          radius: ring.radiusM,
          color: ring.strokeColor,
          weight: 1.5,
          opacity: ring.strokeOpacity,
          fillColor: ring.fillColor,
          fillOpacity: ring.fillOpacity,
        })
          .bindTooltip(
            isDfsAipGeometryComplete()
              ? escapeHtml(ring.label)
              : `${escapeHtml(ring.label)} \u2014 vorl\u00e4ufig`,
            { sticky: true },
          )
          .addTo(layer);
      }
      L.marker([a.lat, a.lon], {
        icon: L.divIcon({
          className: "ctr-band-pin",
          html: `<div style="background:rgba(20,30,48,0.85);color:#fff;font-size:10px;font-weight:700;padding:1px 5px;border-radius:6px;border:1px solid rgba(255,255,255,0.6);white-space:nowrap">${escapeHtml(a.icao)}</div>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      })
        .bindPopup(
          `<div style="max-width:240px"><b>CTR ${escapeHtml(a.name)}</b><br/>` +
            `<span style="font-size:12px;color:#475569">${escapeHtml(a.avInfo?.model === "dfs2026zonen" ? (isDfsAipGeometryComplete() ? "DFS-Zonen 1\u20134 (Geometrie je CTR im AIP)" : "DFS-Zonen 1\u20134 (Geometrie vorl\u00e4ufig)") : (isDfsAipGeometryComplete() ? "Geometrie je CTR im AIP" : "Geometrie je CTR im AIP (vorl\u00e4ufig)"))}</span>` +
            renderCtrHeightStaffel(a.avInfo) +
            `</div>`,
        )
        .addTo(layer);
    }
  }, [enabled, mapVersion]);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const r = await geocodeSearch(search.trim());
      setResults(r);
      if (r[0]) {
        const lat = parseFloat(r[0].lat);
        const lon = parseFloat(r[0].lon);
        mapRef.current?.setView([lat, lon], 13);
        setPoint({ lat, lon });
      }
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  function pickResult(r: GeocodeResult) {
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    mapRef.current?.setView([lat, lon], 13);
    setPoint({ lat, lon });
    setResults([]);
    setSearch(r.display_name);
  }

  function locateMe() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        mapRef.current?.setView([lat, lon], 14);
        setPoint({ lat, lon });
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }

  // Beim ersten Aufruf der Karten-Seite einmalig den aktuellen Standort
  // ermitteln. Schlägt fehl (keine Berechtigung, kein GPS), bleibt die
  // Standardansicht (Mitte Deutschland) – der Pilot kann jederzeit selbst
  // auf "Mein Standort" tippen oder eine Adresse suchen.
  const didAutoLocate = useRef(false);
  useEffect(() => {
    if (didAutoLocate.current) return;
    if (point) return; // bereits ein Punkt gesetzt (z.B. aus localStorage)
    didAutoLocate.current = true;
    locateMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Baut für die eingebetteten Fremd-Karten (Dipul, Geoportail, BAZL) eine
  // URL, die – sofern ein Punkt ausgewählt wurde – auf diesen Punkt zentriert.
  // Funktioniert per bekannten URL-Parametern; falls die Seite die Parameter
  // ignoriert, kann der Pilot zusätzlich den eingebauten "Mein Standort"-
  // Knopf der jeweiligen Karte nutzen (geolocation ist via `allow` freigegeben).
  function externalMapUrl(id: TabId, baseUrl: string): string {
    if (!point) return baseUrl;
    const lat = point.lat;
    const lon = point.lon;
    try {
      if (id === "dipul") {
        // Dipul akzeptiert lat/lon/zoom als Query-Parameter.
        return `${baseUrl}?lat=${lat.toFixed(5)}&lon=${lon.toFixed(5)}&zoom=14`;
      }
      if (id === "geoportail-lu") {
        // Geoportail Luxembourg nimmt X (lon) / Y (lat) als WGS84.
        return `${baseUrl}?zoom=14&X=${lon.toFixed(5)}&Y=${lat.toFixed(5)}`;
      }
      if (id === "swissdrones") {
        // map.geo.admin.ch akzeptiert WGS84 via crosshair=…&E=lon&N=lat
        // (intern wird nach LV95 umgerechnet, wenn die Werte im WGS84-Bereich
        // liegen).
        const sep = baseUrl.includes("?") ? "&" : "?";
        return `${baseUrl}${sep}E=${lon.toFixed(5)}&N=${lat.toFixed(5)}&zoom=10&crosshair=marker`;
      }
    } catch {}
    return baseUrl;
  }

  const enabledCount = useMemo(() => Object.values(enabled).filter(Boolean).length, [enabled]);

  return (
    <div className="space-y-4">
      <form onSubmit={handleSearch} className="card flex flex-col sm:flex-row gap-2">
        <input
          className="input"
          placeholder="Adresse oder Ort suchen …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="input-map-search"
        />
        <button type="submit" className="btn-primary" disabled={searching} data-testid="button-map-search">
          {searching ? "Suche …" : "Suchen"}
        </button>
        <button type="button" className="btn-secondary" onClick={locateMe} data-testid="button-map-locate">
          Mein Standort
        </button>
      </form>

      {results.length > 1 && (
        <div className="card">
          <div className="text-sm font-semibold mb-2">Treffer</div>
          <ul className="divide-y divide-[var(--color-app-border)]">
            {results.map((r, i) => (
              <li key={i}>
                <button
                  onClick={() => pickResult(r)}
                  className="w-full text-left py-2 text-sm hover:bg-[var(--color-app-bg-secondary)]"
                >
                  {r.display_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-1 border-b border-[var(--color-app-border)] overflow-x-auto" role="tablist">
        <TabButton id="interactive" active={activeTab} onSelect={setActiveTab}>
          Interaktive Karte
        </TabButton>
        {EXTERNAL_MAPS.map((m) => (
          <TabButton key={m.id} id={m.id} active={activeTab} onSelect={setActiveTab}>
            {m.label}
          </TabButton>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="card p-0 overflow-hidden relative">
          <div
            ref={containerRef}
            style={{ height: 560, width: "100%", display: activeTab === "interactive" ? "block" : "none" }}
            data-testid="map-container"
          />
          {activeTab === "interactive" && enabled["de_kontrollzonen"] ? (
            <div
              className="absolute top-3 right-3 z-[1000] rounded-lg border border-[var(--color-app-border)] bg-white/95 shadow-md text-[var(--color-app-text)] max-w-[230px]"
              data-testid="ctr-legend"
            >
              <button
                type="button"
                className="flex w-full items-center gap-1.5 px-3 py-2 text-left"
                onClick={() => setCtrLegendOpen((v) => !v)}
                data-testid="button-ctr-legend-toggle"
              >
                <span className="text-xs">{ctrLegendOpen ? "\u25be" : "\u25b8"}</span>
                <span className="text-xs font-semibold">{"CTR-H\u00f6henb\u00e4nder"}</span>
              </button>
              {ctrLegendOpen ? (
                <div className="px-3 pb-2.5 space-y-1.5">
                  {ctrLegendEntries.map((entry) => (
                    <div key={entry.label} className="flex items-center gap-2">
                      <span
                        className="inline-block h-3.5 w-3.5 rounded-sm border"
                        style={{
                          borderColor: entry.strokeColor,
                          backgroundColor: entry.fillColor,
                          opacity: Math.max(entry.fillOpacity, 0.6),
                        }}
                      />
                      <span className="text-[11px] leading-tight">{entry.label}</span>
                    </div>
                  ))}
                  <div className="text-[10px] italic text-[var(--color-app-text-secondary)] pt-0.5">
                    {getCtrProvisionalNote()}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {activeTab === "interactive" && vectorHint ? (
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-1.5 rounded-full border border-[var(--color-app-border)] bg-white/95 px-3 py-1.5 shadow-md text-[var(--color-app-text)]"
              data-testid="hint-zoom-geozones"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
              <span className="text-xs font-medium">{"Hineinzoomen, um Geozonen anzuzeigen"}</span>
            </div>
          ) : null}
          {EXTERNAL_MAPS.map((m) =>
            activeTab === m.id ? (
              <iframe
                // Key enthält Koordinaten, damit das iframe nach Klick auf
                // "Mein Standort" / Suche neu geladen wird und auf den
                // gewählten Punkt zentriert.
                key={`${m.id}-${point ? `${point.lat.toFixed(4)},${point.lon.toFixed(4)}` : "default"}`}
                src={externalMapUrl(m.id, m.url)}
                title={m.label}
                allow="geolocation; fullscreen"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ height: 560, width: "100%", border: 0, display: "block" }}
                data-testid={`iframe-${m.id}`}
              />
            ) : null
          )}
        </div>
        <div className="space-y-4">
          <div className="card">
            <div className="font-semibold mb-1">Auswahlpunkt</div>
            {point ? (
              <>
                <div className="text-sm text-[var(--color-app-text)]" data-testid="text-map-address">{address}</div>
                <div className="text-xs text-[var(--color-app-text-secondary)] mt-1">
                  {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
                </div>
                <div className="text-xs text-[var(--color-app-text-secondary)] mt-2">
                  NOTAMs:{" "}
                  {notamsQuery.isLoading
                    ? "lade …"
                    : notamsQuery.isError
                    ? "Fehler"
                    : `${notamsQuery.data?.rows.length ?? 0} aktiv`}
                </div>
              </>
            ) : (
              <div className="text-sm text-[var(--color-app-text-secondary)]">Tippe auf die Karte, um einen Punkt zu wählen.</div>
            )}
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold">Layer</div>
              <div className="text-xs text-[var(--color-app-text-secondary)]">{enabledCount} aktiv</div>
            </div>
            <div className="mb-3 pb-3 border-b border-[var(--color-app-border)]">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInfrastructure}
                  onChange={(e) => setShowInfrastructure(e.target.checked)}
                  data-testid="checkbox-layer-energy-infrastructure"
                />
                <span className="text-[var(--color-app-text)]">Zusätzliche Anlagen (Wind, Leitungen, Masten)</span>
              </label>
              <div className="text-xs text-[var(--color-app-text-secondary)] mt-1">
                Nur bei aktivierter Ebene, ab Zoom {INFRASTRUCTURE_MIN_ZOOM} und in freigeschalteten Ländern; Suchradius 5 km. Tippe auf einen Treffer für Quelle und amtlichen Abgleich.
              </div>
              {showInfrastructure ? (
                <div className="text-xs text-[var(--color-app-text-secondary)] mt-1" data-testid="energy-infrastructure-status">
                  {!infrastructureCenter
                    ? "Lade Kartenausschnitt …"
                    : !infrastructureIso
                      ? "Für diesen Kartenausschnitt ist kein Land erkannt."
                      : !infrastructureCountry
                        ? `Für ${infrastructureIso} sind keine Zusatzquellen konfiguriert.`
                        : infrastructureCountryLocked
                          ? `${infrastructureCountry.name} ist für dieses Konto nicht freigeschaltet.`
                          : infrastructureCenter.zoom < INFRASTRUCTURE_MIN_ZOOM
                            ? `Zum Anzeigen näher heranzoomen (mindestens Zoom ${INFRASTRUCTURE_MIN_ZOOM}).`
                            : energyInfrastructureQuery.isLoading
                              ? "Lade Zusatzanlagen …"
                              : energyInfrastructureQuery.isError
                                ? "Zusatzanlagen konnten nicht geladen werden; ein fehlender Treffer belegt keine Anlagenfreiheit."
                                : energyInfrastructureQuery.data
                                  ? `Anlagenabgleich im Umkreis von ${energyInfrastructureQuery.data.searchRadiusKm} km. Quelle und Details beim Antippen.`
                                  : "In diesem Ausschnitt wurden keine Zusatztreffer geladen."}
                </div>
              ) : null}
              <div className="mt-3 pt-3 border-t border-[var(--color-app-border)]">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAllGeozones}
                    onChange={(e) => { const v = e.target.checked; setShowAllGeozones(v); saveGeozoneShowAll(v); }}
                    data-testid="checkbox-show-all-geozones"
                  />
                  <span className="text-[var(--color-app-text)]">Alle Geozonen anzeigen (auch über {DEFAULT_REACHABLE_ALTITUDE_M} m)</span>
                </label>
                <div className="text-xs text-[var(--color-app-text-secondary)] mt-1">
                  Aus: Vektor-Zonen (AT/BE/DK), deren Untergrenze über {DEFAULT_REACHABLE_ALTITUDE_M} m liegt, werden ausgeblendet.
                </div>
              </div>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              <div>
                <div className="text-xs font-semibold text-[var(--color-app-text-secondary)] uppercase tracking-wide mb-1">
                  Tiefflugstrecken (DE)
                </div>
                <ul className="space-y-1">
                  <li>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!enabled["de_lfa"]}
                        onChange={(e) => setEnabled((prev) => ({ ...prev, de_lfa: e.target.checked }))}
                        data-testid="checkbox-layer-de_lfa"
                      />
                      <span className="text-[var(--color-app-text)]">LFA Bundeswehr-Tiefflug (250 ft AGL)</span>
                    </label>
                  </li>
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold text-[var(--color-app-text-secondary)] uppercase tracking-wide mb-1">
                  Kategorien (länderübergreifend)
                </div>
                <ul className="space-y-1">
                  {GEOZONE_CATEGORIES.map((cat) => (
                    <li key={cat.id}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!enabled[cat.id]}
                          onChange={(e) => setEnabled((prev) => ({ ...prev, [cat.id]: e.target.checked }))}
                          data-testid={`checkbox-category-${cat.id}`}
                        />
                        <span className="text-[var(--color-app-text)]">{cat.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold text-[var(--color-app-text-secondary)] uppercase tracking-wide mb-1">
                  Länder-Gesamtanzeige
                </div>
                <ul className="space-y-1">
                  {COMBINED_COUNTRY_TOGGLES.map((t) => (
                    <li key={t.id}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!enabled[t.id]}
                          onChange={(e) => setEnabled((prev) => ({ ...prev, [t.id]: e.target.checked }))}
                          data-testid={`checkbox-combined-${t.id}`}
                        />
                        <span className="text-[var(--color-app-text)]">{t.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

async function showFeatureInfoPopup(
  map: L.Map,
  latlng: L.LatLng,
  containerPoint: L.Point,
  enabled: Record<string, boolean>
) {
  const active = LAYER_GROUPS.flatMap((g) => {
    const activeNames = new Set(
      filterActiveWmsLayers(g.country, g.layers.map((l) => l.layer), enabled),
    );
    return g.layers
      .filter((l) => activeNames.has(l.layer))
      .map((l) => ({ ...l, country: g.country }));
  });
  if (active.length === 0) return;
  const size = map.getSize();
  const bounds = map.getBounds();
  // Project to EPSG:3857 (web mercator)
  const sw = L.CRS.EPSG3857.project(bounds.getSouthWest());
  const ne = L.CRS.EPSG3857.project(bounds.getNorthEast());
  const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;

  const popup = L.popup({ maxWidth: 360 }).setLatLng(latlng);
  popup.setContent(`<div style="font-size:12px;color:#64748b">Lade Detailinformationen …</div>`).openOn(map);

  const fragments: string[] = [];
  let hitCount = 0;
  for (const layer of active) {
    try {
      const country = layer.country as Country;
      const infoFormat = layer.infoFormat || infoFormatForCountry(country);
      const url = `/api/proxy/wms-info?baseUrl=${encodeURIComponent(layer.baseUrl)}&layer=${encodeURIComponent(
        layer.layer
      )}&bbox=${encodeURIComponent(bbox)}&width=${size.x}&height=${size.y}&x=${Math.round(
        containerPoint.x
      )}&y=${Math.round(containerPoint.y)}&crs=EPSG:3857&infoFormat=${encodeURIComponent(infoFormat)}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) continue;
      const raw = (await r.text()).trim();
      if (!raw) continue;
      const safe = renderFeatureInfo(raw, country);
      if (!safe) continue;
      fragments.push(
        `<div style="margin-bottom:8px"><b>${escapeHtml(layer.label)}</b><div style="font-size:12px">${safe}</div></div>`
      );
      // Kuratierte Regulierungsinfo (Definition, Betriebsvoraussetzung, Hinweise,
      // Links, Kontakte) aus der geteilten Datenbasis GEOZONE_REGULATORY_INFO —
      // jetzt für alle Länder (DE/CH/LU/NL/FR), nicht mehr nur FR. Wo
      // zonenspezifische Kontakte existieren (ED-R/CTR), werden diese statt der
      // Fallback-Kontakte gezeigt.
      const regKey = REG_INFO_KEY_BY_LAYER[layer.id];
      const info = regKey ? GEOZONE_REGULATORY_INFO[regKey] : undefined;
      if (info) {
        const zoneName = extractZoneName(raw);
        const contacts = getContactsForLayerId(regKey, zoneName);
        const curated = renderCuratedInfo(info, contacts);
        if (curated) fragments.push(curated);
        if (regKey === "kontrollzonen") {
          const staffel = renderCtrHeightStaffel();
          if (staffel) fragments.push(staffel);
        }
      }
      hitCount++;
    } catch {
      /* ignore */
    }
    if (hitCount >= 4) break;
  }
  if (fragments.length === 0) {
    popup.setContent(
      `<div style="font-size:12px;color:#64748b">Keine Geozonen-Informationen für diesen Punkt — zoome näher heran oder aktiviere weitere Layer.</div>`
    );
    return;
  }
  popup.setContent(`<div style="max-height:300px;overflow:auto">${fragments.join("")}</div>`);
}

// Rendert die kuratierte Regulierungsinfo (gleiche Datenbasis wie Mobile,
// GEOZONE_REGULATORY_INFO) als sicheres, escapetes HTML für das Karten-Popup.
function renderCuratedInfo(info?: GeozoneRegulatoryInfo, contactsOverride?: GeozoneContact[]): string {
  if (!info) return "";
  const nl2br = (s: string) => escapeHtml(s).replace(/\n/g, "<br/>");
  const sections: string[] = [];
  sections.push(`<div style="font-weight:600;margin-bottom:2px">${escapeHtml(info.title)}</div>`);
  if (info.referenz) {
    sections.push(`<div style="font-size:11px;color:#64748b;margin-bottom:6px">${escapeHtml(info.referenz)}</div>`);
  }
  const field = (label: string, value?: string) =>
    value ? `<div style="margin-bottom:6px"><b>${escapeHtml(label)}:</b> ${nl2br(value)}</div>` : "";
  sections.push(field("Definition", info.definition));
  sections.push(field("Abstandsregel", info.abstandsregel));
  sections.push(field("Betriebsvoraussetzung", info.betriebsvoraussetzung));
  sections.push(field("Hinweis", info.hinweis));
  if (info.links && info.links.length > 0) {
    const items = info.links
      .map(
        (l) =>
          `<li><a href="${escapeHtml(l.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(l.label)}</a></li>`
      )
      .join("");
    sections.push(`<div style="margin-bottom:6px"><b>Links:</b><ul style="margin:2px 0 0 16px;padding:0">${items}</ul></div>`);
  }
  const contacts = contactsOverride ?? info.contacts;
  if (contacts && contacts.length > 0) {
    const items = contacts
      .map((c) => {
        const v = escapeHtml(c.value);
        const label = escapeHtml(c.label);
        if (c.type === "email") return `<li>${label}: <a href="mailto:${v}">${v}</a></li>`;
        if (c.type === "phone") return `<li>${label}: <a href="tel:${v}">${v}</a></li>`;
        return `<li>${label}: <a href="${v}" target="_blank" rel="noopener noreferrer">${v}</a></li>`;
      })
      .join("");
    sections.push(`<div style="margin-bottom:6px"><b>Kontakte:</b><ul style="margin:2px 0 0 16px;padding:0">${items}</ul></div>`);
  }
  return `<div style="margin-bottom:8px;padding-top:8px;border-top:1px solid #e2e8f0;font-size:12px">${sections.join("")}</div>`;
}

// Rendert die 4-Zonen-Einteilung des neuen DFS-CTR-Modells (NfL 2026-1-3960,
// gültig ab 06.08.2026, nur an den 15 Flughäfen mit DFS-Flugplatzkontrolle)
// aus der GETEILTEN Logik (ctrAltitude.ts), damit Web und Mobile dieselben
// Werte/Texte zeigen. Die örtliche Zonen-Geometrie steht in Ergänzungen zur
// AIP GERMANY bzw. auf dipul.
function renderCtrHeightStaffel(avInfo?: CtrAvInfo): string {
  const model = avInfo?.model ?? "dfs2026zonen";
  const steps = avInfo?.heightSteps ?? DFS_ZONEN_HEIGHT_STEPS;
  const lines = describeHeightSteps(steps);
  if (lines.length === 0) return "";
  const header =
    model === "dfs2026zonen"
      ? `<div style="font-weight:600;margin-bottom:2px">DFS-Zonen 1–4 (NfL 2026-1-3960${isDfsZonenEffective() ? "" : ", gültig ab 6.8.2026"})</div>` +
        `<div style="font-size:11px;color:#64748b;margin-bottom:6px">Gilt nur in CTR der 15 Flughäfen mit DFS-Flugplatzkontrolle · Zonen-Geometrie je Flughafen: AIP GERMANY/dipul · EU-Grenze ${DFS_CTR_MAX_HEIGHT_AGL_M + 20} m bleibt</div>`
      : model === "dfs2026"
        ? `<div style="font-weight:600;margin-bottom:2px">Außen-/Innenbereich (${escapeHtml(avInfo?.nflReference ?? "NfL 2026-1-3884")})</div>` +
          `<div style="font-size:11px;color:#64748b;margin-bottom:6px">Geometrie je CTR im AIP GERMANY${isDfsAipGeometryComplete() ? "" : " (Umstellung bis Herbst 2026)"}</div>`
        : `<div style="font-weight:600;margin-bottom:2px">Allgemeinverfügung${avInfo?.nflReference ? ` (${escapeHtml(avInfo.nflReference)})` : ""}</div>`;
  const items = lines.map((l) => `<li>${escapeHtml(l)}</li>`).join("");
  return (
    `<div style="margin-bottom:8px;padding-top:8px;border-top:1px solid #e2e8f0;font-size:12px">` +
    header +
    `<ul style="margin:2px 0 0 16px;padding:0">${items}</ul>` +
    `</div>`
  );
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

// Versucht den Zonennamen (z. B. "CTR Frankfurt Main", "ED-R 146 ...") aus der
// WMS-GetFeatureInfo-HTML zu lesen, damit zonenspezifische Kontakte (ED-R/CTR
// via getContactsForLayerId) aufgelöst werden können. Liefert undefined, wenn
// kein Namensfeld gefunden wird — dann greifen die Fallback-Kontakte.
const ZONE_NAME_KEY = /^(name|bezeichnung|zone|titel|title|nom|designation|gebiet)$/i;
function extractZoneName(html: string): string | undefined {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return undefined;
  }
  const normKey = (s: string) => s.trim().replace(/[:\s]+$/, "");
  for (const table of Array.from(doc.querySelectorAll("table"))) {
    // Zweispaltige key/value-Zeilen
    for (const tr of Array.from(table.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("td")).map((td) => (td.textContent || "").trim());
      if (cells.length === 2 && ZONE_NAME_KEY.test(normKey(cells[0])) && cells[1]) {
        return cells[1];
      }
    }
    // Tabellen mit Kopfzeile (th) + Datenzeile
    const headers = Array.from(table.querySelectorAll("th")).map((th) => (th.textContent || "").trim());
    const idx = headers.findIndex((h) => ZONE_NAME_KEY.test(normKey(h)));
    if (idx >= 0) {
      for (const tr of Array.from(table.querySelectorAll("tr"))) {
        const cells = Array.from(tr.querySelectorAll("td")).map((td) => (td.textContent || "").trim());
        if (cells.length > idx && cells[idx]) return cells[idx];
      }
    }
  }
  return undefined;
}

function TabButton({
  id,
  active,
  onSelect,
  children,
}: {
  id: TabId;
  active: TabId;
  onSelect: (id: TabId) => void;
  children: React.ReactNode;
}) {
  const isActive = active === id;
  return (
    <button
      role="tab"
      onClick={() => onSelect(id)}
      className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
        isActive
          ? "border-brand-500 text-brand-700"
          : "border-transparent text-[var(--color-app-text-secondary)] hover:text-[var(--color-app-text)]"
      }`}
      data-testid={`tab-${id}`}
    >
      {children}
    </button>
  );
}
